import clsx from "clsx";
import { Check, Plus } from "lucide-react";
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { SyncBadge } from "./SyncBadge";
import type { SyncOption } from "./SyncSelect";

/*
 * Adding records to a list (a group's items, a bundle's contents): a search bar ("+ Add an item:
 * search..."; the + says it adds, not that it searches the list), its results dropping down under
 * it as you type, each with its own Add. Adding is done there and then (it shows in the list at
 * once) and the row changes in place: "✓ Added", its button Undo, for a mis-click. The results stay
 * open to add more; a click outside closes them (focusing the bar opens them again), Escape clears
 * it. A result can carry a note about what adding it does (it's in another group) and need a second
 * click (Confirm). No question first: an Add button beside the name is the choice itself.
 */

/** Matches listed at most (keep typing to narrow them down). */
const SHOWN = 50;

/** A record that can be added, and what adding it does, if it's worth saying. */
export interface AddOption extends SyncOption {
    note?: string;
    /** The note in full, on hover (when the note is short). */
    noteTitle?: string;
    /** Adding it takes a second click (its Add turns into Confirm first): e.g. it moves it from another group. */
    confirm?: boolean;
}

export function AddPicker(
    props: Readonly<{
        /** What it's for, e.g. "Add an item: search by name or item number". */
        placeholder: string;
        /** What can be added now (those already in the list left out). */
        options: AddOption[];
        onAdd: (value: string) => Promise<unknown>;
        /** Taking back one just added (a mis-click). None: "Added" can't be undone here. */
        onUndo?: (value: string) => Promise<unknown>;
        /** Its results listed in place under it, not dropping down over what's below (inside a popup: WindowPopup), and the cursor in it. */
        inline?: boolean;
        /**
         * The options are in order (e.g. the most used first): listed so, not by name, and all of them (the first few) as soon as
         * it's clicked into, before anything's typed.
         */
        ranked?: boolean;
    }>,
) {
    const [query, setQuery] = useState("");
    // Clicked away: the results close, what's typed stays (focusing the bar opens them again).
    const [shut, setShut] = useState(false);
    // Clicked into (ranked: its options show before anything's typed).
    const [focused, setFocused] = useState(false);
    // Ranked: where each option came in the order (kept for one added, which leaves the options), so it stays in its place.
    const ranks = useRef(new Map<string, number>());
    if (props.ranked) props.options.forEach((o, i) => ranks.current.set(o.value, i));
    const box = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(0);
    // Added (or being added) while it's open: they leave `options` once in the list, so they're kept here to show as Added.
    const [added, setAdded] = useState<AddOption[]>([]);
    // The one whose Add has turned into Confirm (it needs a second click), if any.
    const [armed, setArmed] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const matches = useMemo(() => {
        const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
        if (!words.length && !(props.ranked && focused)) return [];
        // Those added stay where they were (by name, or ranked: in their place), changed in place to Added: not moved to the end.
        const rank = (o: AddOption) => ranks.current.get(o.value) ?? Number.MAX_SAFE_INTEGER;
        const all = [...props.options, ...added.filter((a) => !props.options.some((o) => o.value === a.value))].sort((a, b) =>
            props.ranked ? rank(a) - rank(b) : a.label.localeCompare(b.label),
        );
        return all.filter((o) => words.every((w) => `${o.label} ${o.detail ?? ""}`.toLowerCase().includes(w)));
    }, [props.options, props.ranked, added, query, focused]);
    // Added here, and still in the list: one taken out again some other way (its own Take out) can be added again.
    const isAdded = (value: string) => added.some((a) => a.value === value) && !props.options.some((o) => o.value === value);
    const open = props.inline || ((query.trim() !== "" || (props.ranked === true && focused)) && !shut);
    useEffect(() => {
        if (!open || props.inline) return;
        const onDown = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setShut(true);
        document.addEventListener("pointerdown", onDown);
        return () => document.removeEventListener("pointerdown", onDown);
    }, [open, props.inline]);

    // One at a time (every button waits while one is saving): the next save is built from the list as it's then saved, so two quick adds can't overwrite each other.
    const run = (value: string, work: () => Promise<unknown>, after: () => void) => {
        setBusy(value);
        setError(null);
        work()
            .then(after)
            .catch((err: Error) => setError(err.message))
            .finally(() => setBusy(null));
    };
    // Once each: one taken out some other way (its own Take out) and added again is still just the one.
    const add = (o: AddOption) => run(o.value, () => props.onAdd(o.value), () => setAdded((list) => [...list.filter((a) => a.value !== o.value), o]));
    // Add: at once, or (one that needs it) Confirm first, then add.
    const choose = (o: AddOption) => {
        if (o.confirm && armed !== o.value) return setArmed(o.value);
        setArmed(null);
        add(o);
    };
    const undo = (o: AddOption) => {
        const onUndo = props.onUndo;
        if (onUndo) run(o.value, () => onUndo(o.value), () => setAdded((list) => list.filter((a) => a.value !== o.value)));
    };

    const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const last = Math.min(matches.length, SHOWN) - 1;
            setActive((i) => (e.key === "ArrowDown" ? Math.min(i + 1, last) : Math.max(i - 1, 0)));
        } else if (e.key === "Enter") {
            // Enter adds the highlighted one (not one already added).
            const match = matches[active];
            if (match && !isAdded(match.value) && busy === null) choose(match);
        } else if (e.key === "Escape" && query) {
            // Clear it, not close a window it's in; a new search starts afresh.
            e.preventDefault();
            e.stopPropagation();
            setQuery("");
            setAdded([]);
            setArmed(null);
        }
    };

    return (
        <div ref={box} className="relative">
            <label className="relative block">
                <span className="sr-only">{props.placeholder}</span>
                <Plus className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted" />
                <input
                    className="input pl-9"
                    placeholder={props.placeholder}
                    value={query}
                    role="combobox"
                    aria-expanded={open}
                    aria-autocomplete="list"
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setActive(0);
                        setArmed(null);
                        setShut(false);
                    }}
                    onFocus={() => {
                        setShut(false);
                        setFocused(true);
                    }}
                    onKeyDown={onKey}
                    autoFocus={props.inline}
                />
            </label>
            {open ? (
                <div className={props.inline ? "mt-2 min-h-24" : "card absolute right-0 left-0 z-50 mt-1 max-h-80 overflow-y-auto p-1"} role="listbox">
                    {props.inline && query.trim() === "" ? <p className="m-0 px-2.5 py-2 text-sm text-muted">Type a name or item number.</p> : null}
                    {matches.slice(0, SHOWN).map((o, i) => (
                        <div key={o.value} className={clsx("flex items-center gap-2 rounded px-2.5 py-1.5", i === active && "bg-surface-2")} onMouseEnter={() => setActive(i)}>
                            <SyncBadge synced={o.synced} />
                            {/* Two lines on every row, so none changes height: the name; then its detail (item number) and its note, or "✓ Added". */}
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm">{o.label}</span>
                                <span className="flex min-w-0 items-center gap-1 text-xs text-muted">
                                    {o.detail ? <span className="shrink-0">{o.detail}</span> : null}
                                    {o.detail && (isAdded(o.value) || o.note) ? <span aria-hidden="true">·</span> : null}
                                    {isAdded(o.value) ? (
                                        <span className="inline-flex shrink-0 items-center gap-1 font-medium text-brand-ink">
                                            <Check className="size-3.5" /> Added
                                        </span>
                                    ) : (
                                        <span className="truncate" title={o.noteTitle}>
                                            {o.note}
                                        </span>
                                    )}
                                    {/* Keeps the line's height when it has nothing to show. */}
                                    {!o.detail && !o.note && !isAdded(o.value) ? <span>&nbsp;</span> : null}
                                </span>
                            </span>
                            <AddButton added={isAdded(o.value)} armed={armed === o.value} busy={busy !== null} canUndo={Boolean(props.onUndo)} onAdd={() => choose(o)} onUndo={() => undo(o)} />
                        </div>
                    ))}
                    {query.trim() !== "" && matches.length === 0 ? <p className="m-0 px-2.5 py-2 text-sm text-muted">Nothing matches.</p> : null}
                    {matches.length > SHOWN ? <p className="m-0 px-2.5 py-2 text-xs text-muted">{matches.length - SHOWN} more: keep typing to narrow them down.</p> : null}
                    {error ? <p className="m-0 px-2.5 py-1.5 text-sm text-danger">{error}</p> : null}
                </div>
            ) : null}
        </div>
    );
}

/**
 * A result's button, the same size whatever it says: Add (white), Confirm (green: clicked once, when
 * it needs a second), then Undo once added (soft red: it takes it back out; the row says "Added").
 */
function AddButton(props: Readonly<{ added: boolean; armed: boolean; busy: boolean; canUndo: boolean; onAdd: () => void; onUndo: () => void }>) {
    const size = "w-[4.5rem] shrink-0 justify-center px-2 py-0.5 text-xs";
    if (!props.added) {
        return (
            <button type="button" className={clsx(size, props.armed ? "btn-primary" : "btn-secondary")} disabled={props.busy} onClick={props.onAdd}>
                {props.armed ? "Confirm" : "Add"}
            </button>
        );
    }
    if (!props.canUndo) return <span className={size} />;
    return (
        <button type="button" className={clsx(size, "btn-danger")} disabled={props.busy} title="Take it back out" onClick={props.onUndo}>
            Undo
        </button>
    );
}
